import { io, Socket } from 'socket.io-client';

/**
 * Servicio de WebSocket para notificaciones en tiempo real
 * Singleton que mantiene una única conexión activa
 */
class WebSocketService {
  private socket: Socket | null = null;
  private isConnected = false;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private listeners: Map<string, Set<(...args: any[]) => void>> = new Map();
  private connectionCheckInterval: NodeJS.Timeout | null = null;
  private lastActivityTime = Date.now();
  private inactivityTimeout = 30 * 60 * 1000; // 30 minutos
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private isManuallyDisconnected = false;

  /**
   * Conectar al servidor de WebSocket
   */
  connect() {
    if (this.socket && this.isConnected) {
      return;
    }

    this.isManuallyDisconnected = false;

    // Obtener URL base sin el prefijo /api/v1
    let serverUrl = import.meta.env.VITE_API_URL || 'http://localhost:3009';
    // Remover /api/v1 si está presente
    serverUrl = serverUrl.replace('/api/v1', '');

    const wsUrl = serverUrl; // Usar namespace raíz

    this.socket = io(wsUrl, {
      withCredentials: true, // CRÍTICO: Enviar cookies HTTP-only automáticamente
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: this.maxReconnectAttempts,
    });

    this.setupListeners();
    this.startConnectionMonitoring();
    this.startHeartbeat();
  }

  /**
   * Configurar listeners del socket
   */
  private setupListeners() {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      this.isConnected = true;
      this.reconnectAttempts = 0;
      this.lastActivityTime = Date.now();
    });

    this.socket.on('connected', (data) => {
    });

    this.socket.on('disconnect', (reason) => {
      this.isConnected = false;
    });

    this.socket.on('connect_error', (error) => {
      this.reconnectAttempts++;
      
      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        this.disconnect();
      }
    });

    this.socket.on('error', (error) => {
      // WebSocket error
    });

    // Reenviar todos los eventos a los listeners registrados
    this.socket.onAny((eventName, ...args) => {
      this.lastActivityTime = Date.now();
      this.notifyListeners(eventName, ...args);
    });
  }

  /**
   * Desconectar del servidor
   */
  disconnect() {
    this.isManuallyDisconnected = true;
    this.stopConnectionMonitoring();
    this.stopHeartbeat();
    
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      this.listeners.clear();
    }
  }

  /**
   * Suscribirse a un evento específico
   */
  on(event: string, callback: (...args: any[]) => void) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback);

    // Retornar función para desuscribirse
    return () => {
      const eventListeners = this.listeners.get(event);
      if (eventListeners) {
        eventListeners.delete(callback);
        if (eventListeners.size === 0) {
          this.listeners.delete(event);
        }
      }
    };
  }

  /**
   * Desuscribirse de un evento
   */
  off(event: string, callback?: (...args: any[]) => void) {
    if (!callback) {
      this.listeners.delete(event);
      return;
    }

    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      eventListeners.delete(callback);
      if (eventListeners.size === 0) {
        this.listeners.delete(event);
      }
    }
  }

  /**
   * Notificar a todos los listeners de un evento
   */
  private notifyListeners(event: string, ...args: any[]) {
    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      eventListeners.forEach((callback) => {
        try {
          callback(...args);
        } catch (error) {
          // Error in event listener
        }
      });
    }
  }

  /**
   * Emitir un evento al servidor
   */
  emit(event: string, data?: any) {
    if (this.socket && this.isConnected) {
      this.socket.emit(event, data);
    }
  }

  /**
   * Verificar si está conectado
   */
  getIsConnected(): boolean {
    return this.isConnected;
  }

  /**
   * Obtener el ID del socket actual
   */
  getSocketId(): string | undefined {
    return this.socket?.id;
  }

  /**
   * Iniciar monitoreo de conexión y detección de inactividad
   */
  private startConnectionMonitoring() {
    this.stopConnectionMonitoring();
    
    this.connectionCheckInterval = setInterval(() => {
      const now = Date.now();
      const timeSinceLastActivity = now - this.lastActivityTime;
      
      // Si ha pasado mucho tiempo sin actividad y no está desconectado manualmente
      if (timeSinceLastActivity > this.inactivityTimeout && !this.isManuallyDisconnected) {
        this.reconnect();
      }
      
      // Verificar si la conexión sigue activa
      if (this.socket && !this.socket.connected && !this.isManuallyDisconnected) {
        this.reconnect();
      }
    }, 60000); // Verificar cada minuto
  }

  /**
   * Detener monitoreo de conexión
   */
  private stopConnectionMonitoring() {
    if (this.connectionCheckInterval) {
      clearInterval(this.connectionCheckInterval);
      this.connectionCheckInterval = null;
    }
  }

  /**
   * Iniciar heartbeat para mantener la conexión activa
   */
  private startHeartbeat() {
    this.stopHeartbeat();
    
    this.heartbeatInterval = setInterval(() => {
      if (this.socket && this.isConnected) {
        this.socket.emit('ping');
        this.lastActivityTime = Date.now();
      }
    }, 30000); // Ping cada 30 segundos
  }

  /**
   * Detener heartbeat
   */
  private stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  /**
   * Reconectar manualmente
   */
  private reconnect() {
    if (this.isManuallyDisconnected) {
      return;
    }
    
    this.disconnect();
    
    // Esperar un poco antes de reconectar
    setTimeout(() => {
      if (!this.isManuallyDisconnected) {
        this.connect();
      }
    }, 2000);
  }

  /**
   * Verificar si está conectado
   */
  isConnectedToServer(): boolean {
    return this.isConnected && this.socket?.connected === true;
  }

  /**
   * Obtener estadísticas de conexión
   */
  getConnectionStats() {
    return {
      isConnected: this.isConnected,
      socketId: this.socket?.id,
      reconnectAttempts: this.reconnectAttempts,
      lastActivity: new Date(this.lastActivityTime),
      timeSinceLastActivity: Date.now() - this.lastActivityTime,
      isManuallyDisconnected: this.isManuallyDisconnected,
    };
  }
}

// Exportar instancia singleton
export const websocketService = new WebSocketService();

